import { db } from '../firebase/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';

/**
 * @fileoverview Servicio para comparación dinámica de planes
 * Lee directamente desde Firestore (AdminPlans) - NO hardcodeado
 */

/**
 * Convierte keys técnicos a nombres amigables en español
 */
export function getFeatureFriendlyName(featureKey: string): string {
  const friendlyNames: Record<string, string> = {
    // Grupos y Miembros
    families: '👨‍👩‍👧‍👦 Grupos Familiares',
    members: '👤 Integrantes',
    admins: '👑 Administradores',
    
    // Actividades y Tareas
    activities: '🏃 Actividades',
    checklists: '📋 Tareas y Checklists',
    
    // Recompensas
    rewards: '🎁 Sistema de Recompensas',
    
    // Cocina
    recipes: '🍳 Recetas',
    mealPlans: '🍽️ Planificador de Comidas',
    shoppingLists: '🛒 Listas de Compras',
    
    // Educación
    evaluations: '📊 Evaluaciones',
    subjects: '📚 Materias',
    groups: '🏫 Grupos Escolares',
    
    // IA y Avanzado
    aiGenerationsPerMonth: '🤖 Generador IA (Recetas y Eventos)',
    aiEnabled: '🤖 Asistente con Inteligencia Artificial',
    
    // Calendario
    calendarSync: '📅 Sincronización de Calendario',
    calendarIntegration: '🔗 Integración con Google Calendar',
    calendar_recurrence_advanced: '🔄 Eventos Recurrentes Avanzados',
    calendar_limitless: '📅 Eventos Ilimitados en Calendario',
    
    // Almacenamiento
    storageGB: '💾 Almacenamiento',
    imageUploads: '📷 Subida de Imágenes',
    
    // Colaboración
    sharedLists: '🤝 Listas Compartidas',
    familyGoals: '🎯 Metas Familiares',
    
    // Notificaciones
    pushNotifications: '🔔 Notificaciones Push',
    emailReminders: '📧 Recordatorios por Email',
    
    // Reportes
    reports: '📊 Reportes y Estadísticas',
    analytics: '📈 Análisis Detallado',
    
    // Soporte
    prioritySupport: '💬 Soporte Prioritario',
    customization: '🎨 Personalización Avanzada',
  };
  
  return friendlyNames[featureKey] || featureKey;
}

/**
 * Determina si un feature debe mostrarse al usuario
 * (filtra features técnicos o internos)
 */
export function shouldShowFeature(featureKey: string): boolean {
  // Features principales que SÍ se muestran
  const userFacingFeatures = [
    'families', 'members', 'admins',
    'activities', 'checklists', 'rewards',
    'recipes', 'mealPlans', 'shoppingLists',
    'evaluations', 'subjects', 'groups',
    'aiGenerationsPerMonth', 'aiEnabled',
    'reports', 'analytics',
    'prioritySupport'
  ];
  
  // Si está en la lista de user-facing, mostrar
  if (userFacingFeatures.includes(featureKey)) {
    return true;
  }
  
  // Ocultar features técnicos/internos que el usuario no necesita ver
  const hiddenFeatures = [
    'calendar_recurrence_advanced',
    'calendar_limitless',
    'calendarSync',
    'calendarIntegration',
    'storageGB',
    'imageUploads',
    'pushNotifications',
    'emailReminders',
    'sharedLists',
    'familyGoals',
    'customization'
  ];
  
  return !hiddenFeatures.includes(featureKey);
}

export interface PlanLimits {
  families?: number;
  members?: number;
  admins?: number;
  activities?: number;
  checklists?: number;
  rewards?: number;
  recipes?: number;
  mealPlans?: number;
  shoppingLists?: number;
  evaluations?: number;
  subjects?: number;
  groups?: number;
  aiGenerationsPerMonth?: number;
  [key: string]: number | undefined;
}

export interface PlanInfo {
  id: string;
  name: string;
  price: number;
  priceMonthly?: number;
  priceYearly?: number;
  limits: PlanLimits;
  features: string[];
  displayFeatures: string[];
}

export interface FeatureComparison {
  feature: string;
  featureName: string; // Nombre legible
  oldLimit: number;
  newLimit: number;
  status: 'unchanged' | 'limited' | 'blocked' | 'unlimited_to_limited' | 'limited_to_blocked';
  needsSelection: boolean; // True si el usuario debe elegir qué mantener
  selectionLimit?: number; // Cuántos puede mantener
}

export interface PlanComparisonResult {
  fromPlan: PlanInfo;
  toPlan: PlanInfo;
  changes: FeatureComparison[];
  hasLimitations: boolean;
  requiresWizard: boolean;
}

/**
 * Obtiene información de un plan desde Firestore
 */
export async function getPlanInfo(planId: string): Promise<PlanInfo | null> {
  try {
    const planDoc = await getDoc(doc(db, 'subscriptionPlans', planId));
    
    if (!planDoc.exists()) {
      console.error(`Plan ${planId} no encontrado en Firestore`);
      return null;
    }
    
    const data = planDoc.data();
    
    return {
      id: planDoc.id,
      name: data.name || 'Unknown Plan',
      price: data.price || 0,
      priceMonthly: data.priceMonthly,
      priceYearly: data.priceYearly,
      limits: data.limits || {},
      features: data.features || [],
      displayFeatures: data.displayFeatures || [],
    };
  } catch (error) {
    console.error('Error obteniendo plan:', error);
    return null;
  }
}

/**
 * Interpreta el valor de un límite
 * -1 = bloqueado
 * 0 = ilimitado
 * > 0 = limitado a ese número
 */
function interpretLimit(limit: number | undefined): 'blocked' | 'unlimited' | 'limited' {
  if (limit === undefined) return 'blocked';
  if (limit === -1) return 'blocked';
  if (limit === 0) return 'unlimited';
  return 'limited';
}

/**
 * Determina si una feature necesita selección del usuario
 */
function needsUserSelection(
  oldLimit: number | undefined,
  newLimit: number | undefined,
  currentCount: number
): boolean {
  console.log(`🔍 needsUserSelection - oldLimit: ${oldLimit}, newLimit: ${newLimit}, currentCount: ${currentCount}`);
  
  // Si el nuevo límite es bloqueado (-1), no hay selección (todo se pausa)
  if (newLimit === -1 || newLimit === undefined) {
    console.log('  → Bloqueado o undefined, no necesita selección');
    return false;
  }
  
  // Si el nuevo límite es ilimitado (0), no hay selección
  if (newLimit === 0) {
    console.log('  → Ilimitado, no necesita selección');
    return false;
  }
  
  // Si el límite anterior era ilimitado (0) y ahora es limitado (>0)
  if ((oldLimit === 0 || oldLimit === undefined) && newLimit > 0) {
    // Solo si el usuario tiene más items que el nuevo límite
    const needs = currentCount > newLimit;
    console.log(`  → De ilimitado a limitado: currentCount (${currentCount}) > newLimit (${newLimit}) = ${needs}`);
    return needs;
  }
  
  // Si ambos son limitados, verificar si el nuevo es menor
  if (oldLimit && oldLimit > 0 && newLimit > 0) {
    const needs = newLimit < oldLimit && currentCount > newLimit;
    console.log(`  → Ambos limitados: newLimit (${newLimit}) < oldLimit (${oldLimit}) && currentCount (${currentCount}) > newLimit = ${needs}`);
    return needs;
  }
  
  console.log('  → No cumple ninguna condición, no necesita selección');
  return false;
}

/**
 * Compara dos planes y retorna las diferencias
 */
export async function comparePlans(
  fromPlanId: string,
  toPlanId: string,
  currentCounts?: Partial<Record<keyof PlanLimits, number>>
): Promise<PlanComparisonResult | null> {
  try {
    const fromPlan = await getPlanInfo(fromPlanId);
    const toPlan = await getPlanInfo(toPlanId);
    
    if (!fromPlan || !toPlan) {
      console.error('No se pudieron obtener los planes para comparar');
      return null;
    }
    
    const changes: FeatureComparison[] = [];
    
    // Obtener todas las features únicas de ambos planes
    const allFeatures = new Set([
      ...Object.keys(fromPlan.limits),
      ...Object.keys(toPlan.limits),
    ]);
    
    for (const feature of allFeatures) {
      const oldLimit = fromPlan.limits[feature] ?? -1;
      const newLimit = toPlan.limits[feature] ?? -1;
      
      // Determinar el estado del cambio
      let status: FeatureComparison['status'] = 'unchanged';
      
      const oldType = interpretLimit(oldLimit);
      const newType = interpretLimit(newLimit);
      
      if (oldType !== newType) {
        if (newType === 'blocked') {
          status = oldType === 'limited' ? 'limited_to_blocked' : 'blocked';
        } else if (oldType === 'unlimited' && newType === 'limited') {
          status = 'unlimited_to_limited';
        } else if (oldType === 'limited' && newType === 'limited' && newLimit < oldLimit) {
          status = 'limited';
        }
      } else if (oldType === 'limited' && newType === 'limited' && newLimit < oldLimit) {
        status = 'limited';
      }
      
      // Determinar si necesita selección del usuario
      const currentCount = currentCounts?.[feature as keyof PlanLimits] || 0;
      const needsSelection = needsUserSelection(oldLimit, newLimit, currentCount);
      
      // Solo agregar si hay cambio
      if (status !== 'unchanged' || needsSelection) {
        changes.push({
          feature,
          featureName: getFeatureFriendlyName(feature),
          oldLimit,
          newLimit,
          status,
          needsSelection,
          selectionLimit: newType === 'limited' ? newLimit : undefined,
        });
      }
    }
    
    // Ordenar por prioridad (las que necesitan selección primero)
    changes.sort((a, b) => {
      if (a.needsSelection && !b.needsSelection) return -1;
      if (!a.needsSelection && b.needsSelection) return 1;
      return 0;
    });
    
    return {
      fromPlan,
      toPlan,
      changes,
      hasLimitations: changes.length > 0,
      requiresWizard: changes.some(c => c.needsSelection || c.status === 'limited' || c.status === 'blocked'),
    };
  } catch (error) {
    console.error('Error comparando planes:', error);
    return null;
  }
}

/**
 * Obtiene una descripción legible del cambio de límite
 */
export function getChangeDescription(change: FeatureComparison): string {
  const { featureName, oldLimit, newLimit, status } = change;
  
  const formatLimit = (limit: number) => {
    if (limit === -1) return 'Bloqueado';
    if (limit === 0) return 'Ilimitado';
    return `Hasta ${limit}`;
  };
  
  switch (status) {
    case 'blocked':
    case 'limited_to_blocked':
      return `${featureName}: ${formatLimit(oldLimit)} → Bloqueado ⏸️`;
    
    case 'unlimited_to_limited':
      return `${featureName}: Ilimitado → ${formatLimit(newLimit)} 📊`;
    
    case 'limited':
      return `${featureName}: ${formatLimit(oldLimit)} → ${formatLimit(newLimit)} ⚠️`;
    
    default:
      return `${featureName}: Sin cambios`;
  }
}

/**
 * Obtiene un mensaje de ayuda para el usuario sobre qué hacer
 */
export function getHelpMessage(change: FeatureComparison): string {
  const { featureName, newLimit, needsSelection } = change;
  
  if (needsSelection) {
    return `Debes seleccionar ${newLimit} ${featureName.toLowerCase()} para mantener activos. Los demás se pausarán (no se borran).`;
  }
  
  if (newLimit === -1) {
    return `${featureName} no está disponible en el plan gratuito. Todos se pausarán automáticamente (no se borran).`;
  }
  
  return `${featureName} tiene un nuevo límite de ${newLimit}.`;
}

/**
 * Obtiene el total de items que necesitan ser pausados
 */
export function getItemsToBePaused(
  change: FeatureComparison,
  currentCount: number
): number {
  if (change.newLimit === -1) return currentCount; // Todo se pausa
  if (change.newLimit === 0) return 0; // Nada se pausa
  return Math.max(0, currentCount - change.newLimit);
}
